import { Calendar, Check, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

const BOOKING_URL = "https://home.shortcutssoftware.com/ah#/_h/sites/6509/details";

const treatsAreas = [
  'Sagging cheeks and jawline',
  'Fine lines and deep wrinkles',
  'Loose skin on the neck and under the chin',
  'Hooded eyes and drooping brow',
  'Skin laxity on arms, abdomen, thighs, and more',
];

const benefits = [
  'Non-invasive with no needles or downtime',
  'Immediate visible results with continued improvement over 3-6 months',
  'Naturally lifts and contours the skin',
  'Suitable for all skin types and tones',
  'Walk-in, walk-out procedure',
];

export default function HIFU() {
  return (
    <main className="min-h-screen bg-[#FFFDF9]" data-testid="hifu-page">
      <SEO page="hifu" />

      {/* Hero */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-32">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1519415387722-a1c3bbef716c?w=1920&h=800&fit=crop"
            alt="HIFU face lifting treatment Camberwell"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#FFFDF9] via-[#FFFDF9]/95 to-[#FFFDF9]/70" />
        </div>

        <div className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-[#8D7B68]/10 border border-[#8D7B68]/30 rounded-full px-4 py-2 mb-6">
              <span className="text-sm text-[#5D4A3A] font-medium">Non-Surgical Face Lifting</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-medium text-[#2C2420] leading-[1.1] mb-6">
              HIFU Facelifting
            </h1>
            <p className="text-xl text-[#8D7B68] italic mb-4">
              "A Natural Lift for a New You"
            </p>
            <p className="text-lg text-[#5D4A3A] leading-relaxed mb-8">
              The natural ageing process takes its toll on everyone, leading to skin sagging and laxity that looks and feels overcome by the pull of gravity. HIFU treats areas and skin troubles that were previously considered 'untouchable' without surgery.
            </p>
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
              <Button className="bg-[#8D7B68] hover:bg-[#6B5D4D] text-white rounded-full px-8 py-6 text-lg" data-testid="hifu-book-hero-btn">
                <Calendar className="w-5 h-5 mr-2" />
                Book HIFU Consultation
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-[#8D7B68]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24 text-center text-white">
          <h2 className="text-2xl font-medium mb-3">How HIFU Works</h2>
          <p className="text-white/80 max-w-3xl mx-auto text-base md:text-lg">
            With the support of ultrasound technology, focused energy is delivered deep beneath the skin's surface to stimulate collagen and elastin production. This targets the SMAS layer — the same layer treated during surgical facelifts — smoothing, tightening and lifting the skin for a more youthful look over time.
          </p>
        </div>
      </section>

      {/* What HIFU Treats + Benefits side by side */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* What it Treats */}
            <div>
              <p className="text-sm uppercase tracking-widest text-[#8D7B68] mb-4">Targeted Areas</p>
              <h2 className="text-4xl md:text-5xl font-medium text-[#2C2420] mb-8">
                What HIFU Treats
              </h2>
              <div className="space-y-4">
                {treatsAreas.map((area) => (
                  <div key={area} className="flex items-start gap-3 bg-white p-4 rounded-lg border border-[#D4C4B0]/50">
                    <div className="w-6 h-6 rounded-full bg-[#8D7B68]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-4 h-4 text-[#8D7B68]" />
                    </div>
                    <p className="text-[#2C2420] font-medium">{area}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Benefits */}
            <div>
              <p className="text-sm uppercase tracking-widest text-[#4A5D44] mb-4">Why Choose HIFU</p>
              <h2 className="text-4xl md:text-5xl font-medium text-[#2C2420] mb-8">
                Benefits
              </h2>
              <div className="space-y-4">
                {benefits.map((benefit) => (
                  <div key={benefit} className="flex items-start gap-3 bg-white p-4 rounded-lg border border-[#D4C4B0]/50">
                    <div className="w-6 h-6 rounded-full bg-[#4A5D44]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-4 h-4 text-[#4A5D44]" />
                    </div>
                    <p className="text-[#2C2420] font-medium">{benefit}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Cartridges & Treatment Detail */}
      <section className="py-24 md:py-32 bg-[#E8DED2]/30">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-sm uppercase tracking-widest text-[#8D7B68] mb-4">Precision Technology</p>
              <h2 className="text-4xl md:text-5xl font-medium text-[#2C2420] mb-6">
                Customised Cartridge Depths
              </h2>
              <p className="text-lg text-[#5D4A3A] leading-relaxed mb-6">
                All HIFU machines work on the same core principle — what makes the difference is the cartridges. At Artistry House, we use a range of cartridges from 1.5mm to 18mm depending on the treatment area and your skin's unique needs.
              </p>
              <p className="text-lg text-[#5D4A3A] leading-relaxed mb-6">
                Shallower cartridges (1.5mm-3mm) target fine lines and delicate areas like around the eyes, while deeper cartridges (4.5mm-18mm) are used for body treatments, jawline contouring, and areas with more significant laxity.
              </p>
              <p className="text-lg text-[#5D4A3A] leading-relaxed">
                The amount of treatments required depends on the degree of skin laxity, platysmal skin banding, jawline definition, eye drop, wrinkle folds, neck folds, and chin fat. These areas are assessed by our clinician using clinical assessment tools during your consultation.
              </p>
            </div>
            <div className="aspect-[4/5] rounded-lg overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1570172619644-dfd03ed5d881?w=600&h=750&fit=crop"
                alt="HIFU skin treatment consultation"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* What to Expect */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <p className="text-sm uppercase tracking-widest text-[#8D7B68] mb-4">Your Experience</p>
            <h2 className="text-4xl md:text-5xl font-medium text-[#2C2420] mb-6">
              What to Expect
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-lg border border-[#D4C4B0]/50 text-center">
              <div className="w-12 h-12 rounded-full bg-[#8D7B68]/10 flex items-center justify-center mx-auto mb-4">
                <span className="text-[#8D7B68] font-bold text-lg">1</span>
              </div>
              <h3 className="text-lg font-medium text-[#2C2420] mb-3">Consultation</h3>
              <p className="text-sm text-[#5D4A3A]">
                Our clinician will assess your skin using clinical tools and recommend the right cartridge depths and treatment plan for your goals.
              </p>
            </div>
            <div className="bg-white p-8 rounded-lg border border-[#D4C4B0]/50 text-center">
              <div className="w-12 h-12 rounded-full bg-[#8D7B68]/10 flex items-center justify-center mx-auto mb-4">
                <span className="text-[#8D7B68] font-bold text-lg">2</span>
              </div>
              <h3 className="text-lg font-medium text-[#2C2420] mb-3">Treatment</h3>
              <p className="text-sm text-[#5D4A3A]">
                HIFU is a comfortable, walk-in walk-out procedure. Most clients experience an immediate tightening effect during the session.
              </p>
            </div>
            <div className="bg-white p-8 rounded-lg border border-[#D4C4B0]/50 text-center">
              <div className="w-12 h-12 rounded-full bg-[#8D7B68]/10 flex items-center justify-center mx-auto mb-4">
                <span className="text-[#8D7B68] font-bold text-lg">3</span>
              </div>
              <h3 className="text-lg font-medium text-[#2C2420] mb-3">Results</h3>
              <p className="text-sm text-[#5D4A3A]">
                Best results are seen after 4 to 12 weeks as collagen rebuilds. Treatment can be repeated 4-12 weeks apart for optimal outcomes.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Ideal For */}
      <section className="py-24 md:py-32 bg-[#2C2420] text-white">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12 lg:px-24 text-center">
          <p className="text-sm uppercase tracking-widest text-[#D4C4B0] mb-4">Is HIFU Right for You?</p>
          <h2 className="text-4xl md:text-5xl font-medium mb-6">
            The Non-Surgical Facelift Alternative
          </h2>
          <p className="text-lg text-[#BCAAA4] max-w-3xl mx-auto mb-8">
            HIFU is ideal for those wanting a non-surgical facelift alternative or who aren't yet ready for invasive procedures but want visible lift and rejuvenation. Return to normal activities immediately after your session — no downtime required.
          </p>
          <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
            <Button className="bg-[#8D7B68] hover:bg-[#6B5D4D] text-white rounded-full px-10 py-6 text-lg" data-testid="hifu-book-cta-btn">
              <Calendar className="w-5 h-5 mr-2" />
              Book Your HIFU Consultation
            </Button>
          </a>
        </div>
      </section>
    </main>
  );
}
